import { useCallback, useEffect, useMemo } from "react";
import { graphql } from "@acto/gql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Todo } from "@acto/taskpane/core/domain";

export const TodoForClientBucket = graphql(`
  fragment TodoForClientBucket on Todo {
    id
    title
    description
    dueDate
    createdAt
    processedStatus
    processedReason
    isWorkflow
    isNoteBased
    assignedUser {
      id
      name
      __typename
      employee {
        id
        name
        __typename
      }
    }
    client {
      id
      legacyId
      name
      externalReference
    }
  }
`);

// One query per bucket: each paginates on its own cursor, which a single combined query cannot do.
const ClientOverdueTodosQuery = graphql(`
  query ClientOverdueTodos($clientId: ID!, $first: Int!, $after: String) {
    clientTodos(clientId: $clientId) {
      overdue(first: $first, after: $after) {
        edges {
          node {
            ...TodoForClientBucket
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const ClientOpenTodosQuery = graphql(`
  query ClientOpenTodos($clientId: ID!, $first: Int!, $after: String) {
    clientTodos(clientId: $clientId) {
      open(first: $first, after: $after) {
        edges {
          node {
            ...TodoForClientBucket
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const ClientCompletedTodosQuery = graphql(`
  query ClientCompletedTodos($clientId: ID!, $first: Int!, $after: String) {
    clientTodos(clientId: $clientId) {
      completed(first: $first, after: $after) {
        edges {
          node {
            ...TodoForClientBucket
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const ClientAssignedToOthersQuery = graphql(`
  query ClientAssignedToOthers($clientId: ID!, $first: Int!, $after: String) {
    clientTodos(clientId: $clientId) {
      assignedToOthers(first: $first, after: $after) {
        edges {
          node {
            ...TodoForClientBucket
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export interface CustomerTodoBucket {
  todos: Todo[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  /** This bucket's own load failure. Buckets fail independently of one another. */
  error: Error | undefined;
  retry: () => void;
  /** True between a `retry()` call and the next result. */
  isRetrying: boolean;
}

export interface CustomerTodosBuckets {
  overdue: CustomerTodoBucket;
  open: CustomerTodoBucket;
  completed: CustomerTodoBucket;
  assignedToOthers: CustomerTodoBucket;
  isPending: boolean;
  error: Error | undefined;
}

/** Four independently paginated buckets, one cursor each. */
export function useCustomerTodosForPage(clientId: string | undefined, limit = 10): CustomerTodosBuckets {
  const variables = useMemo(() => ({ clientId: clientId ?? "" }), [clientId]);
  const enabled = Boolean(clientId);

  const overdue = usePagination({
    query: ClientOverdueTodosQuery,
    limit,
    variables,
    enabled,
    connection: (data) => data.clientTodos.overdue,
  });
  const open = usePagination({
    query: ClientOpenTodosQuery,
    limit,
    variables,
    enabled,
    connection: (data) => data.clientTodos.open,
  });
  const completed = usePagination({
    query: ClientCompletedTodosQuery,
    limit,
    variables,
    enabled,
    connection: (data) => data.clientTodos.completed,
  });
  const assignedToOthers = usePagination({
    query: ClientAssignedToOthersQuery,
    limit,
    variables,
    enabled,
    connection: (data) => data.clientTodos.assignedToOthers,
  });

  // Recap-derived todos bypass the cache resolver, so revalidate each bucket once per client.
  // `refetch` ignores `pause`, hence the `clientId` guard.
  useRevalidateOnClient(clientId, overdue.refetch);
  useRevalidateOnClient(clientId, open.refetch);
  useRevalidateOnClient(clientId, completed.refetch);
  useRevalidateOnClient(clientId, assignedToOthers.refetch);

  const overdueBucket = useBucket(overdue);
  const openBucket = useBucket(open);
  const completedBucket = useBucket(completed);
  const assignedToOthersBucket = useBucket(assignedToOthers);

  return {
    overdue: overdueBucket,
    open: openBucket,
    completed: completedBucket,
    assignedToOthers: assignedToOthersBucket,
    isPending: [overdue, open, completed, assignedToOthers].some(isStillPending),
    error: overdue.error ?? open.error ?? completed.error ?? assignedToOthers.error,
  };
}

// Every bucket selects the same node fragment, so one node type covers all four.
type TodoBucketNode = Parameters<typeof Todo.fromGraphql>[0];

interface BucketPage {
  data?: TodoBucketNode[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  error?: Error;
  refetch?: () => void;
}

function useBucket(page: BucketPage): CustomerTodoBucket {
  const todos = useMemo(() => (page.data ?? []).map((node) => Todo.fromGraphql(node)), [page.data]);
  const pageRefetch = page.refetch;
  const retry = useCallback(() => pageRefetch?.(), [pageRefetch]);

  return {
    todos,
    hasNextPage: page.hasNextPage,
    fetchNextPage: page.fetchNextPage,
    isFetching: page.isFetching,
    error: page.error,
    retry,
    isRetrying: Boolean(page.error) && page.isFetching,
  };
}

function isStillPending(page: { isPending: boolean; error?: Error }): boolean {
  return page.isPending && !page.error;
}

function useRevalidateOnClient(clientId: string | undefined, refetch: (() => void) | undefined) {
  const stableRefetch = useCallback(() => refetch?.(), [refetch]);
  useEffect(() => {
    if (!clientId) return;
    stableRefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);
}
